import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Sparkle, Brain, ChartLineUp, CalendarBlank, ChatCircleDots,
  Robot, Tag, FloppyDisk, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AIScore from "@/components/AIScore";
import MessageBubble from "@/components/MessageBubble";

const STAGES = [
  { value: "nuevo", label: "Nuevo" },
  { value: "contactado", label: "Contactado" },
  { value: "calificado", label: "Calificado" },
  { value: "negociacion", label: "Negociacion" },
  { value: "ganado", label: "Ganado" },
  { value: "perdido", label: "Perdido" },
];

const SENTIMENT_COLORS = {
  positivo: { bg: "rgba(16,185,129,0.12)", text: "#10B981" },
  neutral: { bg: "rgba(161,161,170,0.15)", text: "#A1A1AA" },
  negativo: { bg: "rgba(239,68,68,0.12)", text: "#EF4444" },
};

export default function ContactDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ name: "", phone: "", email: "", stage: "nuevo", tags: "", notes: "" });
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quickMsg, setQuickMsg] = useState("");

  const load = async () => {
    const { data } = await api.get(`/contacts/${id}/full`);
    setData(data);
    const c = data.contact;
    setEdit({
      name: c.name, phone: c.phone, email: c.email || "",
      stage: c.stage, tags: (c.tags || []).join(", "), notes: c.notes || "",
    });
    if (c.ai_analysis) setAnalysis(c.ai_analysis);
  };

  useEffect(() => { load(); }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data: r } = await api.post("/ai/analyze", { contact_id: id });
      setAnalysis(r);
      toast.success("Conversacion analizada con IA");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al analizar");
    } finally { setAnalyzing(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/contacts/${id}`, {
        ...edit, tags: edit.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Contacto actualizado");
      load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const sendQuick = async () => {
    if (!quickMsg.trim()) return;
    try {
      await api.post("/messages", { contact_id: id, text: quickMsg.trim(), sender: "agent" });
      setQuickMsg("");
      load();
      toast.success("Mensaje enviado");
    } catch { toast.error("Error"); }
  };

  if (!data) return <div className="p-8 text-[#A1A1AA] text-sm">Cargando contacto...</div>;
  const { contact, messages, appointments, stats } = data;

  return (
    <div className="p-8 max-w-7xl mx-auto fade-in">
      <Link to="/app/crm" className="inline-flex items-center gap-1.5 text-xs text-[#71717A] hover:text-white mb-6" data-testid="back-to-crm">
        <ArrowLeft size={14} /> Volver a contactos
      </Link>

      <div className="flex items-start gap-6 mb-8">
        <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-2xl font-medium shrink-0">
          {contact.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-4xl font-medium tracking-tighter">{contact.name}</h1>
            <AIScore score={contact.ai_score} size="lg" />
          </div>
          <div className="text-sm text-[#A1A1AA] mt-1 font-mono">{contact.phone}</div>
          {contact.email && <div className="text-sm text-[#71717A] mt-0.5">{contact.email}</div>}
          <div className="flex items-center gap-2 mt-3">
            {(contact.tags || []).map((t) => (
              <span key={t} className="px-2 py-0.5 text-[10px] rounded bg-[#18181B] text-[#A1A1AA] border border-[#27272A]"><Tag size={10} className="inline mr-1" /> {t}</span>
            ))}
          </div>
        </div>
        <Link to={`/app/inbox?contact=${id}`}>
          <Button variant="outline" className="border-[#27272A] bg-[#121214]" data-testid="open-conversation">
            <ChatCircleDots size={14} className="mr-2" /> Abrir conversacion
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Mensajes", value: stats.total_messages, icon: ChatCircleDots },
          { label: "Recibidos", value: stats.inbound, icon: ChatCircleDots },
          { label: "Enviados", value: stats.outbound, icon: PaperPlaneTilt },
          { label: "IA", value: stats.ai_messages, icon: Robot },
        ].map((s) => (
          <div key={s.label} className="border border-[#27272A] bg-[#121214] rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{s.label}</div>
              <s.icon size={14} className="text-[#71717A]" />
            </div>
            <div className="font-display text-3xl font-light tracking-tighter text-white mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* AI Analysis */}
        <div className="lg:col-span-2 border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Brain size={18} className="text-[#5E6AD2]" weight="fill" />
              <h3 className="font-display text-xl font-medium tracking-tight">Analisis IA</h3>
            </div>
            <Button size="sm" onClick={analyze} disabled={analyzing || stats.total_messages === 0} className="bg-[#1A1B3A] hover:bg-[#252762] text-[#9EA9FF] border border-[#3B3F88]" data-testid="analyze-button">
              <Sparkle size={12} className="mr-1.5" weight="fill" /> {analyzing ? "Analizando..." : analysis ? "Re-analizar" : "Analizar conversacion"}
            </Button>
          </div>
          {!analysis && (
            <div className="text-center py-10 text-sm text-[#71717A]">
              {stats.total_messages === 0 ? "Sin mensajes para analizar" : "Aun no analizado. Haz clic en 'Analizar conversacion'."}
            </div>
          )}
          {analysis && (
            <div className="space-y-5 fade-in">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0A0A0C] border border-[#27272A] rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Sentimiento</div>
                  <div className="mt-2">
                    <span className="px-2.5 py-1 rounded text-xs font-medium uppercase tracking-wider" style={{ background: SENTIMENT_COLORS[analysis.sentiment]?.bg || SENTIMENT_COLORS.neutral.bg, color: SENTIMENT_COLORS[analysis.sentiment]?.text || SENTIMENT_COLORS.neutral.text }}>
                      {analysis.sentiment}
                    </span>
                  </div>
                </div>
                <div className="bg-[#0A0A0C] border border-[#27272A] rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Intencion de compra</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-3xl font-light text-white">{analysis.buying_intent}</span>
                    <span className="text-xs text-[#71717A]">/ 100</span>
                  </div>
                  <div className="h-1 bg-[#27272A] rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#10B981]" style={{ width: `${analysis.buying_intent}%` }} />
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0C] border border-[#27272A] rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Resumen</div>
                <div className="mt-2 text-sm text-[#FAFAFA] leading-relaxed">{analysis.summary}</div>
              </div>

              <div className="bg-[#0A0A0C] border border-[#27272A] rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] flex items-center gap-1.5"><ChartLineUp size={12} /> Siguiente accion recomendada</div>
                <div className="mt-2 text-sm text-[#9EA9FF] leading-relaxed">{analysis.next_action}</div>
              </div>

              {analysis.topics && analysis.topics.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Temas detectados</div>
                  <div className="flex flex-wrap gap-2">
                    {analysis.topics.map((t, i) => (
                      <span key={i} className="px-2.5 py-1 text-xs rounded bg-[rgba(94,106,210,0.12)] text-[#9EA9FF] border border-[rgba(94,106,210,0.3)]">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="border border-[#27272A] bg-[#121214] rounded-lg p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Detalles</div>
            <div className="space-y-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Nombre</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" data-testid="edit-name" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Email</Label><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" data-testid="edit-email" /></div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Etapa</Label>
                <Select value={edit.stage} onValueChange={(v) => setEdit({ ...edit, stage: v })}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" data-testid="edit-stage"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Tags</Label><Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} placeholder="vip, urgente..." className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" data-testid="edit-tags" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Notas</Label><Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={4} className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" data-testid="edit-notes" /></div>
              <Button onClick={saveEdit} disabled={saving} size="sm" className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-edit-button">
                <FloppyDisk size={12} className="mr-1.5" weight="fill" /> {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </div>

          <div className="border border-[#27272A] bg-[#121214] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarBlank size={14} className="text-[#71717A]" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Citas</div>
            </div>
            {appointments.length === 0 && <div className="text-xs text-[#71717A]">Sin citas</div>}
            {appointments.map((a) => (
              <div key={a.id} className="text-xs py-2 border-b border-[#18181B] last:border-0">
                <div className="font-medium text-white">{a.title}</div>
                <div className="text-[#71717A] mt-0.5 font-mono">{new Date(a.starts_at).toLocaleString("es-MX")}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#9EA9FF] mt-0.5">{a.status}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick message + recent messages */}
      <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6 mt-6">
        <h3 className="font-display text-xl font-medium tracking-tight mb-4">Historial reciente</h3>
        <div className="max-h-72 overflow-y-auto space-y-2 mb-4 pr-2">
          {messages.length === 0 && <div className="text-sm text-[#71717A]">Sin mensajes</div>}
          {messages.slice(-15).map((m) => {
            const isCustomer = m.direction === "inbound";
            const isAI = m.sender === "ai";
            return (
              <MessageBubble
                key={m.id}
                message={m}
                align={isCustomer ? "left" : "right"}
                variant={isCustomer ? "customer" : isAI ? "ai" : "operator"}
                compact
              />
            );
          })}
        </div>
        <div className="flex gap-2 pt-4 border-t border-[#18181B]">
          <Input value={quickMsg} onChange={(e) => setQuickMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendQuick()} placeholder="Responder rapido..." className="bg-[#0A0A0C] border-[#27272A]" data-testid="quick-message-input" />
          <Button onClick={sendQuick} disabled={!quickMsg.trim()} className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="send-quick-button"><PaperPlaneTilt size={14} weight="fill" /></Button>
        </div>
      </div>
    </div>
  );
}
